import React from 'react';
import { useNavigate } from 'react-router-dom';
import Joyride, { CallBackProps, STATUS, Step, TooltipRenderProps } from 'react-joyride';
import styled, { keyframes, css } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { Settings, User, Mail, Shield, Building2, ArrowRight, X, PartyPopper, Home, XCircle } from 'lucide-react';
import { useTutorialStore } from '@/store/useTutorialStore';

interface AccountSettingsTutorialProps {
  run: boolean;
  onComplete: () => void;
}

/**
 * 계정 설정 튜토리얼 스텝 정의
 */
const steps: Step[] = [
  {
    target: 'body',
    content: 'intro',
    placement: 'center',
    disableBeacon: true,
    disableOverlay: false,
  },
  {
    target: '[data-tutorial="profile-section"]',
    content: 'profile',
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tutorial="settings-grid"]',
    content: 'settings',
    placement: 'top',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: 'body',
    content: 'complete',
    placement: 'center',
    disableBeacon: true,
    disableOverlay: false,
  },
];

/**
 * 스텝별 컨텐츠 정의
 */
const stepContents: Record<string, { icon: React.ReactNode; title: string; description: string }> = {
  intro: {
    icon: <Settings size={32} />,
    title: '계정 설정',
    description: '계정 설정 페이지에 오신 것을 환영합니다!\n\n이곳에서 개인 정보와 보안 설정을 관리할 수 있습니다.',
  },
  profile: {
    icon: <User size={28} />,
    title: '프로필 정보',
    description: '프로필 사진과 기본 정보를 확인할 수 있습니다.\n\n프로필 사진을 클릭하면 새로운 사진으로 변경할 수 있습니다.',
  },
  settings: {
    icon: <Shield size={28} />,
    title: '설정 관리',
    description: '여기서 다양한 설정을 관리할 수 있습니다.\n\n• 개인 정보: 이름, 전화번호\n• 이메일: 계정 이메일 및 인증 상태\n• 계정 보안: 아이디, 비밀번호 변경',
  },
  complete: {
    icon: <PartyPopper size={32} />,
    title: '튜토리얼 완료!',
    description: '축하합니다! 팩트시트의 주요 기능을 모두 알아보았습니다.\n\n이제 팩트시트를 자유롭게 사용해보세요!\n궁금한 점이 있다면 설정에서 튜토리얼을 다시 볼 수 있습니다.',
  },
};

/**
 * 커스텀 툴팁 컴포넌트
 */
const CustomTooltip: React.FC<TooltipRenderProps> = ({
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  isLastStep,
  size,
}) => {
  const navigate = useNavigate();
  const { completeTutorial, completeTutorialAndGoNext } = useTutorialStore();
  const content = stepContents[step.content as string];
  const isCenterStep = step.content === 'intro' || step.content === 'complete';
  const isComplete = step.content === 'complete';
  
  // localStorage에서 온보딩 플로우 여부 직접 확인
  const isOnboarding = localStorage.getItem('factsheet_onboarding_flow') === 'true';

  // 마이스페이스로 이동
  const handleNavigateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    closeProps.onClick(e);
    completeTutorialAndGoNext();
    navigate('/my-space');
  };

  // 튜토리얼 닫기 (완료 처리)
  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    closeProps.onClick(e);
    completeTutorial('account-settings');
  };

  return (
    <TooltipContainer {...tooltipProps} $isCenterStep={isCenterStep} $isComplete={isComplete}>
      <CloseButton {...closeProps}>
        <X size={18} />
      </CloseButton>
      
      <TooltipHeader $isCenterStep={isCenterStep} $isComplete={isComplete}>
        <IconWrapper $isCenterStep={isCenterStep} $isComplete={isComplete} $color="#6366f1">
          {content.icon}
        </IconWrapper>
        <TooltipTitle $isComplete={isComplete}>{content.title}</TooltipTitle>
      </TooltipHeader>
      
      <TooltipContent>
        {content.description.split('\n').map((line, i) => (
          <p key={i}>{line || <br />}</p>
        ))}
      </TooltipContent>
      
      <TooltipFooter>
        <StepIndicator>
          {Array.from({ length: size }, (_, i) => (
            <StepDot key={i} $active={i === index} $color="#6366f1" />
          ))}
        </StepIndicator>
        
        <ButtonGroup>
          {index > 0 && !isComplete && (
            <BackButton {...backProps}>
              이전
            </BackButton>
          )}
          {isLastStep ? (
            <>
              <CloseButtonStyled onClick={handleCloseClick}>
                <XCircle size={16} /> 닫기
              </CloseButtonStyled>
              {isOnboarding && (
                <PrimaryButton onClick={handleNavigateClick} $color="#6366f1" $isComplete={true}>
                  마이스페이스로 이동 <Home size={16} />
                </PrimaryButton>
              )}
            </>
          ) : (
            <PrimaryButton {...primaryProps} $color="#6366f1">
              다음 <ArrowRight size={16} />
            </PrimaryButton>
          )}
        </ButtonGroup>
      </TooltipFooter>
    </TooltipContainer>
  );
};

/**
 * 계정 설정 튜토리얼 컴포넌트
 */
const AccountSettingsTutorial: React.FC<AccountSettingsTutorialProps> = ({ run, onComplete }) => {
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    
    if (finishedStatuses.includes(status)) {
      onComplete();
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress={false}
      showSkipButton
      scrollToFirstStep
      disableOverlayClose={false}
      spotlightClicks
      spotlightPadding={12}
      callback={handleJoyrideCallback}
      tooltipComponent={CustomTooltip}
      floaterProps={{
        styles: {
          floater: {
            filter: 'drop-shadow(0 10px 40px rgba(0, 0, 0, 0.2))',
          },
        },
      }}
      styles={{
        options: {
          arrowColor: '#fff',
          backgroundColor: '#fff',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
          primaryColor: '#6366f1',
          zIndex: 10000,
        },
        spotlight: {
          borderRadius: 16,
        },
      }}
    />
  );
};

// ============================================
// Animations
// ============================================

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const celebrate = keyframes`
  0%, 100% {
    transform: scale(1) rotate(0deg);
  }
  25% {
    transform: scale(1.1) rotate(-5deg);
  }
  75% {
    transform: scale(1.1) rotate(5deg);
  }
`;

// ============================================
// Styled Components
// ============================================

const TooltipContainer = styled.div<{ $isCenterStep?: boolean; $isComplete?: boolean }>`
  position: relative;
  background: #fff;
  border-radius: 20px;
  padding: 24px;
  min-width: ${props => props.$isCenterStep ? '420px' : '340px'};
  max-width: 440px;
  animation: ${fadeIn} 0.3s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  
  ${props => props.$isCenterStep && `
    text-align: center;
    padding: 32px;
  `}
  
  ${props => props.$isComplete && `
    background: linear-gradient(135deg, #fefefe 0%, #f8f7ff 100%);
    border: 2px solid #6366f1;
  `}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: ${semanticColors.background.tertiary};
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.secondary};
    color: ${semanticColors.text.primary};
  }
`;

const TooltipHeader = styled.div<{ $isCenterStep?: boolean; $isComplete?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  
  ${props => props.$isCenterStep && `
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
  `}
`;

const IconWrapper = styled.div<{ $isCenterStep?: boolean; $isComplete?: boolean; $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.$isCenterStep ? '72px' : '52px'};
  height: ${props => props.$isCenterStep ? '72px' : '52px'};
  border-radius: 16px;
  background: ${props => props.$isComplete 
    ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
    : `linear-gradient(135deg, ${props.$color} 0%, ${props.$color}dd 100%)`};
  color: #fff;
  flex-shrink: 0;
  
  ${props => props.$isCenterStep && !props.$isComplete && css`
    animation: ${pulse} 2s ease-in-out infinite;
  `}
  
  ${props => props.$isComplete && css`
    animation: ${celebrate} 1s ease-in-out infinite;
  `}
`;

const TooltipTitle = styled.h3<{ $isComplete?: boolean }>`
  font-size: ${props => props.$isComplete ? '1.5rem' : '1.25rem'};
  font-weight: 700;
  color: ${props => props.$isComplete ? '#6366f1' : semanticColors.text.primary};
  margin: 0;
`;

const TooltipContent = styled.div`
  font-size: 0.9375rem;
  line-height: 1.7;
  color: ${semanticColors.text.secondary};
  margin-bottom: 20px;
  
  p {
    margin: 0;
  }
`;

const TooltipFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid ${semanticColors.border.primary};
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 6px;
`;

const StepDot = styled.div<{ $active: boolean; $color: string }>`
  width: ${props => props.$active ? '20px' : '8px'};
  height: 8px;
  border-radius: 100px;
  background: ${props => props.$active ? props.$color : semanticColors.background.tertiary};
  transition: all 0.3s ease;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const BackButton = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid ${semanticColors.border.primary};
  background: #fff;
  color: ${semanticColors.text.secondary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.tertiary};
    border-color: ${semanticColors.border.secondary};
  }
`;

const CloseButtonStyled = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid ${semanticColors.border.primary};
  background: #fff;
  color: ${semanticColors.text.secondary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.tertiary};
    border-color: ${semanticColors.border.secondary};
    color: ${semanticColors.text.primary};
  }
`;

const PrimaryButton = styled.button<{ $color: string; $isComplete?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  background: ${props => props.$isComplete
    ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
    : `linear-gradient(135deg, ${props.$color} 0%, ${props.$color}dd 100%)`};
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.$isComplete
      ? '0 4px 12px rgba(251, 191, 36, 0.5)'
      : `0 4px 12px ${props.$color}66`};
  }
`;

export default AccountSettingsTutorial;

